// src/components/notify/console/NotifyPublicBroadcastPanel.js
// Instant citizen alert through FlowSense Notify: live to every open public app / site, browser
// push to subscribed citizens, and mirrored into public_alerts for the existing public feed.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Badge, Box, Button, Flex, FormControl, FormLabel, Heading, HStack, IconButton, Input, Select, SimpleGrid, Stack,
    Switch, Text, Textarea, Tooltip, useColorModeValue, useToast,
} from '@chakra-ui/react';
import { MdArchive, MdCampaign } from 'react-icons/md';
import * as api from '../../../services/notifyApi';
import { timeAgo } from '../notifyFormat';

const TYPES = [
    { key: 'info', label: 'Information', color: 'blue' },
    { key: 'maintenance', label: 'Planned maintenance', color: 'cyan' },
    { key: 'warning', label: 'Warning', color: 'orange' },
    { key: 'alert', label: 'Emergency alert', color: 'red' },
];
const EXPIRY = [{ label: 'No expiry', hours: 0 }, { label: '6 hours', hours: 6 }, { label: '1 day', hours: 24 }, { label: '3 days', hours: 72 }];
const INITIAL = { title: '', body: '', type: 'info', area: '', expiryHours: 24, mirror: true };

export default function NotifyPublicBroadcastPanel() {
    const toast = useToast();
    const panel = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const [form, setForm] = useState(INITIAL);
    const [sending, setSending] = useState(false);
    const [list, setList] = useState([]);
    const isMounted = useRef(true);
    useEffect(() => () => { isMounted.current = false; }, []);

    const load = useCallback(async (signal) => {
        try { const rows = await api.listBroadcasts({ limit: 10 }, signal); if (isMounted.current) setList(rows || []); } catch (_) { /* list is secondary */ }
    }, []);
    useEffect(() => { const c = new AbortController(); load(c.signal); return () => c.abort(); }, [load]);

    const send = useCallback(async () => {
        setSending(true);
        try {
            const r = await api.createBroadcast({
                title: form.title, body: form.body, type: form.type, area: form.area || undefined,
                expiresAt: form.expiryHours ? new Date(Date.now() + form.expiryHours * 3600000).toISOString() : undefined,
                mirrorToPublicAlerts: form.mirror,
            });
            toast({ status: 'success', title: 'Citizen alert published', description: `${r.live} open public session(s) · ${r.pushed} browser push`, duration: 4000 });
            if (isMounted.current) setForm(INITIAL);
            load();
        } catch (e) {
            toast({ status: 'error', title: 'Could not publish', description: e.message });
        } finally {
            if (isMounted.current) setSending(false);
        }
    }, [form, toast, load]);

    const archive = useCallback(async (b) => {
        try { await api.archiveBroadcast(b.id); load(); } catch (e) { toast({ status: 'error', title: 'Could not archive', description: e.message }); }
    }, [load, toast]);

    return (
        <Box bg={panel} borderWidth="1px" borderColor={border} borderRadius="2xl" p={{ base: 4, md: 5 }}>
            <HStack mb={1}><MdCampaign /><Heading size="sm">Instant citizen alert</Heading><Badge colorScheme="purple" variant="subtle">FlowSense Notify</Badge></HStack>
            <Text fontSize="sm" color={muted} mb={4}>Live to every open public app and site, browser push to subscribed citizens, and added to the public alerts feed.</Text>
            <Stack spacing={3}>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    <FormControl isRequired><FormLabel fontSize="sm">Title</FormLabel><Input size="sm" value={form.title} maxLength={255} onChange={(e) => setForm(f => ({ ...f, title: e.target.value }))} placeholder="Boil water advisory — Fox Hill" /></FormControl>
                    <FormControl><FormLabel fontSize="sm">Area (optional)</FormLabel><Input size="sm" value={form.area} onChange={(e) => setForm(f => ({ ...f, area: e.target.value }))} placeholder="Fox Hill, Nassau" /></FormControl>
                </SimpleGrid>
                <FormControl isRequired><FormLabel fontSize="sm">Message</FormLabel><Textarea size="sm" rows={3} value={form.body} maxLength={4000} onChange={(e) => setForm(f => ({ ...f, body: e.target.value }))} /></FormControl>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                    <FormControl><FormLabel fontSize="sm">Type</FormLabel><Select size="sm" value={form.type} onChange={(e) => setForm(f => ({ ...f, type: e.target.value }))}>{TYPES.map(t => <option key={t.key} value={t.key}>{t.label}</option>)}</Select></FormControl>
                    <FormControl><FormLabel fontSize="sm">Expires</FormLabel><Select size="sm" value={form.expiryHours} onChange={(e) => setForm(f => ({ ...f, expiryHours: Number(e.target.value) }))}>{EXPIRY.map(o => <option key={o.hours} value={o.hours}>{o.label}</option>)}</Select></FormControl>
                    <FormControl display="flex" alignItems="flex-end" pb={1}><Switch isChecked={form.mirror} onChange={(e) => setForm(f => ({ ...f, mirror: e.target.checked }))} mr={2} aria-label="Add to public alerts feed" /><FormLabel fontSize="sm" mb={0}>Add to public alerts feed</FormLabel></FormControl>
                </SimpleGrid>
                <Flex justify="flex-end"><Button colorScheme={form.type === 'alert' ? 'red' : 'purple'} onClick={send} isLoading={sending} isDisabled={!form.title.trim() || !form.body.trim()}>Publish to citizens</Button></Flex>
            </Stack>
            {list.length > 0 && (
                <Box mt={5}>
                    <Text fontSize="xs" fontWeight="700" color={muted} textTransform="uppercase" mb={2}>Active citizen alerts</Text>
                    <Stack spacing={2}>
                        {list.map(b => {
                            const t = TYPES.find(x => x.key === b.type) || TYPES[0];
                            return (
                                <Flex key={b.id} align="center" gap={2} borderWidth="1px" borderColor={border} borderRadius="lg" px={3} py={2}>
                                    <Badge colorScheme={t.color}>{t.label}</Badge>
                                    <Box flex="1" minW={0}><Text fontSize="sm" fontWeight="600" noOfLines={1}>{b.title}</Text><Text fontSize="xs" color={muted}>{b.area ? `${b.area} · ` : ''}{timeAgo(b.created_at)}</Text></Box>
                                    <Tooltip label="Archive" hasArrow><IconButton size="sm" variant="ghost" icon={<MdArchive />} aria-label={`Archive ${b.title}`} onClick={() => archive(b)} /></Tooltip>
                                </Flex>
                            );
                        })}
                    </Stack>
                </Box>
            )}
        </Box>
    );
}
